// components/Footer.js — navy footer (mockup): брэнд · Бүтээгдэхүүн · Хэрэглэгчийн үйлчилгээ · Холбоо барих
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Instagram, Facebook, Phone, Mail, MapPin } from 'react-feather';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getPageAllList } from 'context/actions/pages';
import i18n from 'i18next';

const localName = (names = []) =>
    names.find((n) => n.langCode === i18n.language)?.name ||
    names.find((n) => n.langCode === 'mn')?.name ||
    '';

// Урд нь '/'-тэй хадгалагдсан url-ийг нормчилж давхар '//' үүсгэхгүй.
const pageHref = (m) =>
    m.url ? '/' + m.url.replace(/^\/+/, '') : '/content/' + m.id;

const FooterColumn = ({ title, children }) => (
    <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper mb-4">
            {title}
        </h4>
        <ul className="space-y-2.5 text-[13px] text-paper/70">{children}</ul>
    </div>
);

const FooterLink = ({ href, children }) => (
    <li>
        <Link href={href} className="hover:text-paper transition-colors">
            {children}
        </Link>
    </li>
);

const Footer = () => {
    const { t } = useTranslation();
    const system = useSelector((state) => state.system);
    const [footerMenus, setFooterMenus] = useState([]);
    const isEn = i18n.language !== 'mn';

    useEffect(() => {
        getMenus();
    }, []);

    const getMenus = async () => {
        const datas = await getPageAllList('?isHeader=0');
        if (datas.status === 'success') setFooterMenus(datas.data || []);
    };

    const categories = system.categories || [];

    return (
        <footer className="bg-ink text-paper">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
                {/* Багана: брэнд · Бүтээгдэхүүн · Хэрэглэгчийн үйлчилгээ · Холбоо барих */}
                <div className="grid grid-cols-2 md:grid-cols-[1.3fr_1fr_1fr_1.3fr] gap-x-8 gap-y-10">
                    {/* Brand */}
                    <div className="col-span-2 md:col-span-1">
                        <Link
                            href="/"
                            className="inline-flex flex-col leading-none select-none"
                            aria-label="Sor Cashmere"
                        >
                            <span className="font-serif font-semibold text-[28px] tracking-[0.12em]">
                                SOR
                            </span>
                            <span className="text-[8px] tracking-[0.42em] uppercase text-paper/80 -mt-0.5">
                                Cashmere
                            </span>
                        </Link>
                        <p className="mt-5 text-[13px] text-paper/70 leading-relaxed max-w-xs">
                            {isEn
                                ? 'Sor Cashmere LLC was founded in 1998 with the initial objective of raw‐material procurement, domestic and international trade, and coarse‐fiber dehairing.'
                                : 'Сор Кашемир ХХК нь 1998 онд анх түүхий эд бэлтгэл, гадаад дотоод худалдаа, хялгас самнах үйлдвэрлэл явуулах зорилготойгоор байгуулагдсан.'}
                        </p>
                        <div className="mt-5 flex gap-2">
                            <Link
                                href="https://www.instagram.com/sor_cashmere?igsh=dWgzc3FyMnAwNHE="
                                target="_blank"
                                aria-label="Instagram"
                                className="w-9 h-9 rounded-full border border-paper/25 flex items-center justify-center hover:bg-paper hover:text-ink transition-colors"
                            >
                                <Instagram size={14} />
                            </Link>
                            <Link
                                href="https://www.facebook.com/share/163znWiMVA/?mibextid=wwXIfr"
                                target="_blank"
                                aria-label="Facebook"
                                className="w-9 h-9 rounded-full border border-paper/25 flex items-center justify-center hover:bg-paper hover:text-ink transition-colors"
                            >
                                <Facebook size={14} />
                            </Link>
                        </div>
                    </div>

                    {/* Shop */}
                    {categories.length > 0 && (
                        <FooterColumn title={t('title.product')}>
                            {categories.map((c) => (
                                <FooterLink
                                    key={c.id}
                                    href={`/products/${c.id}`}
                                >
                                    {localName(c.names) || c.name}
                                </FooterLink>
                            ))}
                        </FooterColumn>
                    )}

                    {/* Customer care */}
                    <FooterColumn title={t('footer.customerService')}>
                        <FooterLink href="/faq">{t('footer.faq')}</FooterLink>
                        <FooterLink href="/gift">
                            {t('footer.giftCard')}
                        </FooterLink>
                        <FooterLink href="/feedback">
                            {t('title.feedback')}
                        </FooterLink>
                        <FooterLink href="/orders">
                            {t('title.my_orders')}
                        </FooterLink>
                        {footerMenus.map((m) => (
                            <FooterLink key={m.id} href={pageHref(m)}>
                                {localName(m.names)}
                            </FooterLink>
                        ))}
                    </FooterColumn>

                    {/* Contact (бодит хаяг — Холбоо барих хуудаснаас) */}
                    <div className="col-span-2 md:col-span-1">
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper mb-4">
                            {isEn ? 'Contact' : 'Холбоо барих'}
                        </h4>
                        <ul className="space-y-3 text-[13px] text-paper/70">
                            <li className="flex items-start gap-2.5">
                                <MapPin size={14} className="mt-0.5 shrink-0" />
                                <span>
                                    {isEn
                                        ? '“Sor Cashmere” factory building, 20th khoroo, Bayangol district, Ulaanbaatar'
                                        : '“Sor Cashmere” үйлдвэрийн байр, 20-р хороо, Баянгол дүүрэг, Улаанбаатар'}
                                </span>
                            </li>
                            <li className="flex items-center gap-2.5">
                                <Phone size={14} className="shrink-0" />
                                <a
                                    href="tel:+97670171917"
                                    className="hover:text-paper transition-colors"
                                >
                                    +976-70171917
                                </a>
                            </li>
                            <li className="flex items-center gap-2.5">
                                <Mail size={14} className="shrink-0" />
                                <a
                                    href="mailto:info@sorcashmere.mn"
                                    className="hover:text-paper transition-colors"
                                >
                                    info@sorcashmere.mn
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-12 pt-5 border-t border-paper/15 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 text-[11px] text-paper/60">
                    <p>
                        © {new Date().getFullYear()} Sor Cashmere LLC.{' '}
                        {t('footer.rights')}
                    </p>
                    {/* Хөгжүүлэгчийн холбоос */}
                    <a
                        href="https://alphafox.mn/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 hover:text-paper transition-colors"
                        aria-label="Alpha Fox Solution"
                    >
                        <span>{isEn ? 'Developed by' : 'Хөгжүүлсэн:'}</span>
                        <Image
                            src="/global/alphafox-logo.png"
                            alt="Alpha Fox Solution"
                            width={20}
                            height={20}
                            className="object-contain opacity-60 group-hover:opacity-100 transition-opacity"
                        />
                        <span className="font-semibold tracking-wide">
                            {isEn
                                ? 'Alpha Fox Solution LLC'
                                : 'Альфа Фокс Солюшн ХХК'}
                        </span>
                    </a>
                    <div className="flex gap-5">
                        <Link
                            href="/faq"
                            className="hover:text-paper transition-colors"
                        >
                            {t('footer.faq')}
                        </Link>
                        <Link
                            href="/contact"
                            className="hover:text-paper transition-colors"
                        >
                            {isEn ? 'Contact' : 'Холбоо барих'}
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
